import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import SocialPosts from "@/components/admin/SocialPosts";
import HealthPanel from "@/components/admin/HealthPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "oxford_admin_token";
const HTTP_UNAUTHORIZED = 401;

const ENDPOINTS = [
  { key: "pickup-requests", label: "Pickups", desc: "Online pickup requests" },
  { key: "vouchers", label: "$10 Vouchers", desc: "Claimed welcome vouchers" },
  { key: "veteran-claims", label: "Veteran Claims", desc: "Free flag & uniform washes" },
  { key: "responder-discounts", label: "Heroes 15%", desc: "Active duty & first responder codes" },
  { key: "contact", label: "Contact Forms", desc: "General contact messages" },
];

function fmtDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return value;
  }
}

function Login({ onSuccess }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": token } });
      sessionStorage.setItem(STORAGE_KEY, token);
      onSuccess(token);
    } catch (err) {
      toast.error(err.response?.status === HTTP_UNAUTHORIZED ? "Invalid password" : "Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="admin-login">
      <Card className="w-full max-w-sm p-8 shadow-xl">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Oxford Cleaners</h1>
          <p className="text-sm text-slate-500 mt-1">Admin sign-in</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin password"
            autoFocus
            data-testid="admin-password-input"
          />
          <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-button">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <p className="text-xs text-slate-400 mt-6 text-center">
          <Link to="/" className="hover:underline">← Back to site</Link>
        </p>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: "bg-amber-100 text-amber-800",
    scheduled: "bg-blue-100 text-blue-800",
    completed: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-slate-200 text-slate-700",
  };
  return (
    <Badge className={`${map[status] || "bg-slate-100 text-slate-700"} font-medium`}>
      {status || "—"}
    </Badge>
  );
}

function RecordCard({ endpoint, record, token, onUpdated }) {
  const isPickup = endpoint === "pickup-requests";
  const isVoucher = endpoint === "vouchers";
  const isVeteran = endpoint === "veteran-claims";
  const isHero = endpoint === "responder-discounts";
  const isContact = endpoint === "contact";
  const [sendingPay, setSendingPay] = useState(false);
  const [paySent, setPaySent] = useState(false);
  const [chargeAmount, setChargeAmount] = useState("");
  const [charging, setCharging] = useState(false);
  const [resending, setResending] = useState(false);

  const resendReceipt = async () => {
    if (!window.confirm(`Resend receipt PDF to ${record.email}?`)) return;
    setResending(true);
    try {
      await axios.post(
        `${API}/admin/pickup-requests/${record.id}/resend-receipt`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Receipt resent to ${record.email}`);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  const sendPaymentLink = async () => {
    setSendingPay(true);
    try {
      await axios.post(
        `${API}/pickup-requests/${record.id}/send-payment-link`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      setPaySent(true);
      toast.success(`Payment link emailed to ${record.email}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Failed to send payment link");
    } finally {
      setSendingPay(false);
    }
  };

  const chargeCard = async () => {
    const amount = parseFloat(chargeAmount);
    if (!amount || amount < 1) {
      toast.error("Enter a charge amount of at least $1.00");
      return;
    }
    if (!window.confirm(`Charge $${amount.toFixed(2)} to ${record.name}'s saved card?`)) return;
    setCharging(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/pickup-requests/${record.id}/charge`,
        { amount_cents: Math.round(amount * 100) },
        { headers: { "X-Admin-Token": token } }
      );
      if (data.status === "succeeded") {
        toast.success(`Charged $${amount.toFixed(2)} — receipt sent to ${record.email}`);
      } else {
        toast.success(`Charge submitted (status: ${data.status})`);
      }
      setChargeAmount("");
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Charge failed");
    } finally {
      setCharging(false);
    }
  };

  return (
    <Card className="p-5 hover:shadow-md transition" data-testid={`record-${record.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-semibold text-slate-900">{record.name || "Unknown"}</div>
          <div className="text-sm text-slate-500">
            {record.phone && <span>{record.phone}</span>}
            {record.phone && record.email && <span className="mx-1.5">·</span>}
            {record.email && <a href={`mailto:${record.email}`} className="hover:underline">{record.email}</a>}
          </div>
        </div>
        {record.status && <StatusBadge status={record.status} />}
      </div>

      {isPickup && (
        <div className="text-sm space-y-1">
          <div><span className="text-slate-500">Address:</span> {record.address}, {record.city}</div>
          <div><span className="text-slate-500">Pickup:</span> {record.pickup_date} · {record.pickup_window}</div>
          <div><span className="text-slate-500">Services:</span> {(record.services || []).join(", ")}</div>
          {record.notes && <div><span className="text-slate-500">Notes:</span> {record.notes}</div>}
          {record.card_last4 && (
            <div className="text-slate-600">
              <span className="text-slate-500">Card on file:</span>{" "}
              <span className="font-medium capitalize">{record.card_brand} •••• {record.card_last4}</span>
            </div>
          )}
          {record.payment_status === "paid" && (
            <div className="pt-2 flex flex-wrap items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-800 font-medium" data-testid={`pay-status-${record.id}`}>
                Paid ${(record.amount_cents / 100).toFixed(2)} · {fmtDate(record.charged_at)}
              </Badge>
              {record.receipt_delivery?.status === "failed" && (
                <Badge
                  className="bg-amber-100 text-amber-800 font-medium"
                  data-testid={`receipt-status-failed-${record.id}`}
                >
                  ⚠ Receipt delivery failed
                  {record.receipt_delivery.retry_count > 1 && (
                    <span className="ml-1 opacity-80">
                      ({record.receipt_delivery.retry_count} attempts)
                    </span>
                  )}
                </Badge>
              )}
              {record.receipt_delivery?.status === "sent" && (
                <Badge
                  variant="outline"
                  className="text-emerald-700 border-emerald-300"
                  data-testid={`receipt-status-sent-${record.id}`}
                >
                  ✓ Receipt delivered
                </Badge>
              )}
              {record.email && (
                <Button
                  size="sm"
                  variant={record.receipt_delivery?.status === "failed" ? "default" : "outline"}
                  className="h-7 text-xs"
                  onClick={resendReceipt}
                  disabled={resending}
                  data-testid={`resend-receipt-${record.id}`}
                >
                  {resending ? "Sending…" : "Resend receipt"}
                </Button>
              )}
            </div>
          )}
          {record.payment_status === "failed" && (
            <div className="pt-2">
              <Badge className="bg-red-100 text-red-800 font-medium" data-testid={`pay-status-${record.id}`}>
                Charge failed
              </Badge>
            </div>
          )}
          {/* Charge controls — only if customer has a card AND not yet paid */}
          {record.user_id && record.card_last4 && record.payment_status !== "paid" && (
            <div className="pt-3 flex items-center gap-2" data-testid={`charge-row-${record.id}`}>
              <span className="text-slate-500">$</span>
              <Input
                type="number"
                step="0.01"
                min="1"
                placeholder="0.00"
                value={chargeAmount}
                onChange={(e) => setChargeAmount(e.target.value)}
                className="h-9 w-28"
                data-testid={`charge-amount-${record.id}`}
              />
              <Button
                size="sm"
                onClick={chargeCard}
                disabled={charging || !chargeAmount}
                data-testid={`charge-button-${record.id}`}
              >
                {charging ? "Charging…" : "Charge card"}
              </Button>
            </div>
          )}
          {record.email && (
            <div className="pt-3">
              <Button
                onClick={sendPaymentLink}
                disabled={sendingPay || paySent}
                size="sm"
                variant={paySent ? "outline" : "default"}
                data-testid={`send-payment-link-${record.id}`}
              >
                {paySent ? "✓ Payment link sent" : sendingPay ? "Sending…" : "Send payment link"}
              </Button>
            </div>
          )}
        </div>
      )}

      {isVoucher && (
        <div className="text-sm">
          <div>
            <span className="text-slate-500">Code:</span>{" "}
            <span className="font-mono font-bold text-emerald-700">{record.code}</span>
            <span className="ml-2 text-slate-500">${record.amount} off</span>
          </div>
          {record.used && <Badge variant="secondary" className="mt-2">Redeemed</Badge>}
        </div>
      )}

      {isVeteran && (
        <div className="text-sm space-y-1">
          <div><span className="text-slate-500">Item:</span> {record.item_type}</div>
          {record.notes && <div><span className="text-slate-500">Notes:</span> {record.notes}</div>}
        </div>
      )}

      {isHero && (
        <div className="text-sm space-y-1">
          <div>
            <span className="text-slate-500">Code:</span>{" "}
            <span className="font-mono font-bold text-blue-700">{record.code}</span>
          </div>
          <div><span className="text-slate-500">Role:</span> {record.role?.replace("_", " ")}</div>
        </div>
      )}

      {isContact && (
        <div className="text-sm space-y-1">
          {record.subject && <div className="font-medium">{record.subject}</div>}
          {record.message && <div className="text-slate-700 whitespace-pre-wrap">{record.message}</div>}
        </div>
      )}

      <div className="text-xs text-slate-400 mt-3">{fmtDate(record.created_at)}</div>
    </Card>
  );
}

function Panel({ endpoint, label, desc, token }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/${endpoint}`, {
        headers: { "X-Admin-Token": token },
      });
      // Sort newest first
      const sorted = [...data].sort((a, b) => {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return tb - ta;
      });
      setItems(sorted);
    } catch (err) {
      setError(err.response?.status === HTTP_UNAUTHORIZED ? "Session expired — please sign in again." : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [endpoint, token]);

  useEffect(() => {
    // Defer to next microtask so the synchronous setState calls inside load()
    // don't trip the set-state-in-effect lint while preserving "load on mount"
    // semantics. Token / endpoint changes still re-trigger via `load` identity.
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, [load]);

  return (
    <div data-testid={`panel-${endpoint}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{label}</h2>
          <p className="text-sm text-slate-500">{desc}</p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" size="sm" data-testid={`refresh-${endpoint}`}>
          {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>
      {error && <div className="rounded bg-red-50 text-red-700 text-sm p-3 mb-3">{error}</div>}
      {!items && !error && <div className="text-slate-500 text-sm">Loading…</div>}
      {items && items.length === 0 && (
        <Card className="p-8 text-center text-slate-500">No entries yet.</Card>
      )}
      {items && items.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((rec) => (
            <RecordCard key={rec.id} endpoint={endpoint} record={rec} token={token} onUpdated={load} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");

  // Validate stored token on mount — if invalid, drop it
  useEffect(() => {
    if (!token) return;
    axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": token } })
      .catch(() => {
        sessionStorage.removeItem(STORAGE_KEY);
        setToken("");
      });
  }, [token]);

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
  };

  if (!token) return <Login onSuccess={setToken} />;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Oxford Cleaners Admin</h1>
            <p className="text-xs text-slate-500">Incoming orders & customer requests</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={async () => {
                try {
                  const { data } = await axios.post(
                    `${API}/admin/email-test`,
                    {},
                    { headers: { "X-Admin-Token": token } }
                  );
                  toast.success(`Test email sent to ${data.to} (id: ${data.resend_id?.slice(0, 8)}…)`, { duration: 8000 });
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Test email failed — check backend logs");
                }
              }}
              variant="outline"
              size="sm"
              data-testid="admin-email-test-button"
              title="Send a test notification email to the owner address"
            >
              Test email
            </Button>
            <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 hover:underline">View site</Link>
            <Button onClick={logout} variant="outline" size="sm" data-testid="admin-logout-button">Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="pickup-requests" className="w-full">
          <TabsList className="w-full flex flex-wrap h-auto">
            {ENDPOINTS.map((e) => (
              <TabsTrigger key={e.key} value={e.key} data-testid={`tab-${e.key}`} className="flex-1 min-w-[120px]">
                {e.label}
              </TabsTrigger>
            ))}
            <TabsTrigger value="social" data-testid="tab-social" className="flex-1 min-w-[120px]">
              Social Posts
            </TabsTrigger>
            <TabsTrigger value="health" data-testid="tab-health" className="flex-1 min-w-[120px]">
              Health
            </TabsTrigger>
          </TabsList>
          {ENDPOINTS.map((e) => (
            <TabsContent key={e.key} value={e.key} className="mt-6">
              <Panel endpoint={e.key} label={e.label} desc={e.desc} token={token} />
            </TabsContent>
          ))}
          <TabsContent value="social" className="mt-6">
            <SocialPosts token={token} />
          </TabsContent>
          <TabsContent value="health" className="mt-6">
            <HealthPanel token={token} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
